import { useState, useEffect } from 'react';
import { useFullPageLoading } from '../../../atoms/full-page-loading/loadingContext';
import { APIRoutes } from '../../../constants';
import { RequestServer } from '../../services';
import { ACTIONS, API_MESSAGES, ApiResponse, ErrorState, InvitationData, UseInvitationValidationReturn } from './helper';



export const useInvitationValidation = (id: string | null): UseInvitationValidationReturn => {
  const { setLoading } = useFullPageLoading();
  
  // State management
  const [data, setData] = useState<InvitationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);

  // Handle different API error scenarios
  const handleApiError = (response: ApiResponse): void => {
    const { message } = response;
    
    switch (message) {
      case API_MESSAGES.INCORRECT_USER:
        setError({
          type: 'INCORRECT_USER',
          title: 'Invalid User',
          message: 'You are not authorized to access this invitation. Please verify your credentials or contact support.',
          showDashboardLink: true
        });
        break;
        
      case API_MESSAGES.NO_LINK_RECORD:
        setError({
          type: 'NO_LINK_RECORD',
          title: 'Invalid Link',
          message: 'The link provided appears to be either invalid or incorrect. Kindly verify the link in your email, or contact your point of contact at Infollion for assistance.',
          showDashboardLink: true
        });
        break;
        
      case API_MESSAGES.PROCESSED_LINK:
        setError({
          type: 'PROCESSED_LINK',
          title: 'Link Already Processed',
          message: 'This link has already been processed. If you need to make changes, please contact support.',
          showDashboardLink: true
        });
        break;
        
      case API_MESSAGES.LINK_EXPIRED:
        setError({
          type: 'LINK_EXPIRED',
          title: 'Link has been expired',
          message: 'This link has already been expired. If you need to make changes, please contact support.',
          showDashboardLink: true
        });
        break;
      case API_MESSAGES.INVITATION_ACCEPTED_AGENDA_NOT_SHARED:
        setError({
          type: 'INVITATION_ACCEPTED_AGENDA_NOT_SHARED',
          title: 'You have already completed necessary steps, Thank You',
          message: 'This link has already been processed. If you need to make changes, please contact support.',
          showDashboardLink: true
        });
        break;
      default:
        setError({
          type: 'UNKNOWN_ERROR',
          title: 'Invalid Link',
          message: 'The link provided appears to be either invalid or incorrect. Kindly verify the link in your email, or contact your point of contact at Infollion for assistance.',
          showDashboardLink: true
        });
    }
  };

  // API call with proper error handling
  const validateInvitationStatus = async (urlId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setLoading(true);
      setError(null);
      setData(null);
      
      const url = `${APIRoutes.peMapping}/project-inviation-agenda-validation`;
      const response: ApiResponse = await RequestServer(url, "POST", {
        url_id: urlId
      });

      if (response.success && response.data && response.data.action === ACTIONS.SHOW_STEPS) {
        setData(response.data);
      } else {
        handleApiError(response);
      }
    } catch (error) {
      console.error('API call failed:', error);
      setError({
        type: 'API_ERROR',
        title: 'Something went wrong',
        message: 'Unable to validate invitation. Please try again later or contact support.',
        showDashboardLink: true
      });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Refetch function for manual retry
  const refetch = () => {
    if (id) {
      validateInvitationStatus(id);
    }
  };

  // Effect to trigger API call when id changes
  useEffect(() => {
    if (id) {
      validateInvitationStatus(id);
    }
  }, [id]);

  return {
    data,
    isLoading,
    error,
    refetch
  };
};